import React, { useState, useEffect } from 'react';

const ScrollToTopButton: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Show button when page is scrolled down
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  // Calculate scroll progress
  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(scrollTop / docHeight, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', updateScrollProgress);
    return () => window.removeEventListener('scroll', updateScrollProgress);
  }, []);

  // Scroll to top function with enhanced smooth transition
  const scrollToTop = () => {
    if (isScrolling) return; // Prevent multiple clicks
    
    setIsScrolling(true);
    
    // Add smooth scroll behavior to html element as fallback
    document.documentElement.style.scrollBehavior = 'smooth';
    document.body.style.scrollBehavior = 'smooth';
    
    // Primary scroll method with smooth behavior
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    
    // Fallback for browsers that don't support smooth scroll
    setTimeout(() => {
      if (window.pageYOffset > 0) {
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }
      // Reset scroll behavior after animation
      document.documentElement.style.scrollBehavior = 'auto';
      document.body.style.scrollBehavior = 'auto';
      setIsScrolling(false);
    }, 1000);
  };

  return (
    <>
      {isVisible && (
        <button
          onClick={scrollToTop}
          disabled={isScrolling}
          className="fixed bottom-12 right-6 sm:bottom-24 sm:right-8 z-50 bg-[#c6d042] hover:bg-[#b8c23a] text-[#1a1e23] w-14 h-14 sm:w-12 sm:h-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 ease-out hover:scale-110 active:scale-95 flex items-center justify-center group touch-manipulation disabled:opacity-70 disabled:cursor-not-allowed"
          aria-label="Scroll to top"
        >
          {/* Circle Progress Ring */}
          <div className="absolute inset-0 rounded-full">
            <svg
              className="w-full h-full transform -rotate-90"
              viewBox="0 0 100 100"
            >
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="42"
                stroke="rgba(26, 30, 35, 0.2)"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
              />
              {/* Progress circle */}
              <circle
                cx="50"
                cy="50"
                r="42"
                stroke="currentColor"
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 42}`}
                strokeDashoffset={`${2 * Math.PI * 42 * (1 - scrollProgress)}`}
                className="transition-all duration-300 ease-out"
              />
            </svg>
          </div>
          
          {/* Arrow Icon */}
          <svg
            className={`w-6 h-6 transition-all duration-300 ${
              isScrolling 
                ? 'animate-pulse' 
                : 'group-hover:-translate-y-1'
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
    </>
  );
};

export default ScrollToTopButton;
